import { t } from 'i18next';
import { useRef } from 'react';
import { ReactComponent as Access } from '../../../assets/img/storage/actions-access.svg';
import { ReactComponent as Copy } from '../../../assets/img/storage/actions-copy.svg';
import { ReactComponent as Download } from '../../../assets/img/storage/actions-dl.svg';
import { ReactComponent as Move } from '../../../assets/img/storage/actions-move.svg';
import { ReactComponent as Remove } from '../../../assets/img/storage/actions-remove.svg';
import { ReactComponent as Rename } from '../../../assets/img/storage/actions-rename.svg';
import { ReactComponent as Share } from '../../../assets/img/storage/actions-share.svg';
import { ReactComponent as Extract, ReactComponent as ExtractAndStay } from '../../../assets/svg/ExtractAndStay.svg';
import { useOutsideAlerter } from '../../../hooks';

export const MENU_WIDTH = 178;
export const MENU_HEIGHT = 300;

interface StorageContextMenuItemProps {
    label: React.ReactNode,
    icon: React.ReactNode,
    action: () => void,
    className: string,
}

function StorageContextMenuItem({ action, className, icon, label, }: StorageContextMenuItemProps) {
    return (
        <div className={`storage__contextmenu-item ${className}`} onClick={action}>
            {icon}
            <span className="storage__contextmenu-item-label">
                {label}
            </span>
        </div>
    )
}

function StorageContextMenuSeparator() {
    return <div className="storage__contextmenu-sep" />
}

interface StorageContextMenuProps {
    top: number,
    left: number,
    selected: string[],
    isOpen: boolean,
    className?: string,
    menuFull?: boolean,
    forbidDelete?: boolean,
    onClose: () => void,
    onDownloadZip?: (v: string) => void
    onDownload?: (v: string[]) => void
    onShare?: (v: string) => void
    onAccess?: (v: string) => void
    onRename?: (v: string) => void
    onCopy?: (v: string[]) => void
    onCopyInMy?: (v: string[]) => void
    onMove?: (v: string[]) => void
    onDelete?: (v: string[]) => void
    onUnzip?: (v: string) => void,
    onZip?: (v: string[]) => void,
}

export function StorageContextMenu({
    selected, top, left, isOpen,
    className, onClose, onDownload, onAccess, onCopy, onShare, onMove, onDelete, onRename, onUnzip, onZip, onCopyInMy, onDownloadZip,
    forbidDelete, menuFull
}: StorageContextMenuProps) {
    const single = selected.length === 1;
    const isFolder = single && selected[0].endsWith('/');
    const isArchive = single && selected[0].endsWith('.zip');

    const wrapperRef = useRef(null);

    useOutsideAlerter(wrapperRef, () => { onClose(); });

    if (!isOpen) return null;

    if (forbidDelete && !(menuFull && (onZip || onCopy || onCopyInMy || onMove || onRename)) && (!single)) return null;

    return (
        <div
            ref={wrapperRef}
            className={`storage__contextmenu ${className ? className : ''} ${isArchive ? '_archive' : ''}`}
            style={{ top: `${top}px`, left: `${left}px` }}
        >
            {single && isFolder && onDownloadZip && <>
                {<StorageContextMenuItem
                    className="_download"
                    action={() => {
                        onDownloadZip(selected[0]); onClose();
                    }}
                    icon={<Download />}
                    label={t('Download')}
                />}
            </>}
            {single && !isFolder && onDownload && <>
                {<StorageContextMenuItem
                    className="_download"
                    action={() => {
                        onDownload(selected); onClose();
                    }}
                    icon={<Download />}
                    label={t('Download')}
                />}

                {((menuFull && (onZip || onCopy || onCopyInMy || onMove || onRename)) || !forbidDelete) && <StorageContextMenuSeparator />}
            </>}
            {menuFull && single && (onShare || (onAccess)) && <>
                {onShare && <StorageContextMenuItem
                    className="_share"
                    action={() => {
                        onShare(selected[0]); onClose();
                    }}
                    label={t('storage:SHARE')}
                    icon={<Share />}
                />
                }
                {onAccess && <StorageContextMenuItem
                    className="_access"
                    action={() => {
                        onAccess(selected[0]); onClose();
                    }}
                    icon={<Access />}
                    label={t('Access')}
                />
                }
                <StorageContextMenuSeparator />
            </>}

            {menuFull && isArchive && <>
                {onUnzip && <StorageContextMenuItem
                    className="_unpack-save"
                    action={() => {
                        onUnzip(selected[0]); onClose();
                    }}
                    icon={<ExtractAndStay />}
                    label={t('storage:EXTRACT')}
                />
                    /* Извлечь и оставить архив */
                }
                {/* <div className="storage__contextmenu-item _unpack-remove" onClick={() => {
                    onUnzip && onUnzip(selected[0]); onClose();
                }}>
                    <ExtractAndDelete />
                    <span className="storage__contextmenu-item-label">Извлечь и удалить архив</span>
                </div> */}
            </>}
            {menuFull && (onZip || onCopy || onCopyInMy || onMove || onRename) && <>
                {onZip && <StorageContextMenuItem className="_copy" action={() => {
                    onZip(selected); onClose();
                }}
                    icon={<Extract />}
                    label={t('storage:ZIP')}
                />
                }
                {onCopyInMy && <StorageContextMenuItem className="_copy" action={() => {
                    onCopyInMy(selected); onClose();
                }}
                    label={t('storage:COPY_TO_MY')}
                    icon={<Copy />}
                />
                }
                {onCopy && <StorageContextMenuItem className="_copy" action={() => {
                    onCopy(selected); onClose();
                }}
                    icon={<Copy />}
                    label={t('storage:COPY_TO')}
                />
                }
                {onMove && <StorageContextMenuItem
                    className="_move"
                    action={() => {
                        onMove(selected); onClose();
                    }}
                    label={t('storage:MOVE')}
                    icon={<Move />}
                />
                }
                {single && onRename &&
                    <StorageContextMenuItem
                        className="_rename"
                        action={() => {
                            onRename(selected[0]); onClose();
                        }}
                        icon={<Rename />}
                        label={t('storage:RENAME')}
                    />
                }
                {!forbidDelete && onDelete && <StorageContextMenuSeparator />}
            </>}
            {!forbidDelete && onDelete && <StorageContextMenuItem
                className="_remove"
                label={t('Delete')}
                icon={<Remove />}
                action={() => { onDelete(selected); onClose(); }}
            />}
        </div>
    )
}

export default StorageContextMenu;
